import { useState, useMemo } from 'react'
import { ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils/cn'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { useCreateProjectMutation } from '../../hooks/use-create-project-mutation'
import type { ProjectMemberRole, ExtraMember } from '../../api/create-project'
import {
  identitySchema,
  metadataSchema,
  scheduleSchema,
  membersSchema,
} from './schema'
import { INITIAL_STATE, STEPS, type WizardState, type StepId } from './types'
import { DynamicIcon } from '@/lib/utils/lucide-icon'
import {
  StepIdentity,
  StepMetadata,
  StepSchedule,
  StepMembers,
  StepReview,
} from './wizard-steps'

interface CreateProjectWizardProps {
  open: boolean
  onClose: () => void
  onCreated?: (projectId: string) => void
}

export function CreateProjectWizard({
  open,
  onClose,
  onCreated,
}: CreateProjectWizardProps) {
  const user = useAuthStore((s) => s.user)
  const isSuperUser = !!user?.isSuperUser
  const [step, setStep] = useState<StepId>(1)
  const [state, setStateRaw] = useState<WizardState>(() => ({
    ...INITIAL_STATE,
    initial_admin_id: isSuperUser ? '' : (user?.id ?? ''),
  }))
  const [errors, setErrors] = useState<Record<string, string>>({})

  const setState = (patch: Partial<WizardState>) =>
    setStateRaw((s) => ({ ...s, ...patch }))

  const mutation = useCreateProjectMutation({
    onSuccess: (project) => {
      onCreated?.(project.id)
      handleClose()
    },
    onError: (err) => {
      setErrors({ _root: err.message })
    },
  })

  const handleClose = () => {
    setStateRaw({
      ...INITIAL_STATE,
      initial_admin_id: isSuperUser ? '' : (user?.id ?? ''),
    })
    setStep(1)
    setErrors({})
    onClose()
  }

  const validate = (target: StepId): boolean => {
    let result
    switch (target) {
      case 1:
        result = identitySchema.safeParse({
          name: state.name,
          code: state.code,
          icon: state.icon,
          color: state.color,
        })
        break
      case 2:
        result = metadataSchema.safeParse({
          description: state.description,
          department: state.department,
          principal_investigator: state.principal_investigator,
          institution: state.institution,
          irb_reference: state.irb_reference,
        })
        break
      case 3:
        result = scheduleSchema.safeParse({
          start_date: state.start_date,
          end_date: state.end_date,
          time_zone: state.time_zone,
          default_language: state.default_language,
          default_calendar: state.default_calendar,
          data_retention_policy: state.data_retention_policy,
          status: state.status,
        })
        break
      case 4:
        result = membersSchema.safeParse({
          initial_admin_id: state.initial_admin_id,
          extra_members: state.extra_members,
        })
        break
      default:
        return true
    }
    if (!result.success) {
      const errs: Record<string, string> = {}
      for (const issue of result.error.issues) {
        errs[issue.path.join('.')] = issue.message
      }
      setErrors(errs)
      return false
    }
    setErrors({})
    return true
  }

  const goNext = () => {
    if (!validate(step)) return
    if (step < 5) setStep((step + 1) as StepId)
  }
  const goBack = () => {
    setErrors({})
    if (step > 1) setStep((step - 1) as StepId)
  }

  const handleSubmit = () => {
    for (const s of [1, 2, 3, 4] as StepId[]) {
      if (!validate(s)) {
        setStep(s)
        return
      }
    }
    mutation.mutate({
      payload: {
        name: state.name.trim(),
        code: state.code.trim() || null,
        icon: state.icon || null,
        color: state.color,
        description: state.description || null,
        department: state.department || null,
        principal_investigator: state.principal_investigator || null,
        institution: state.institution || null,
        irb_reference: state.irb_reference || null,
        start_date: state.start_date || null,
        end_date: state.end_date || null,
        time_zone: state.time_zone,
        default_language: state.default_language,
        default_calendar: state.default_calendar,
        data_retention_policy: state.data_retention_policy || null,
        status: state.status,
        config: {},
      },
      initialAdminId: state.initial_admin_id,
      extraMembers: state.extra_members,
    })
  }

  const excludedFromExtras = useMemo(
    () => [state.initial_admin_id, ...state.extra_members.map((m) => m.user_id)].filter(Boolean),
    [state.initial_admin_id, state.extra_members],
  )

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>ایجاد پروژه جدید</DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-1 border-b border-border pb-3">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex flex-1 items-center gap-1">
              <div
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium',
                  step === s.id
                    ? 'bg-primary text-primary-foreground'
                    : step > s.id
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted text-muted-foreground',
                )}
              >
                {step > s.id ? <Check className="h-3.5 w-3.5" /> : s.id}
              </div>
              <span
                className={cn(
                  'text-xs',
                  step === s.id ? 'font-medium text-foreground' : 'text-muted-foreground',
                )}
              >
                {s.label}
              </span>
              {i < STEPS.length - 1 && <div className="h-px flex-1 bg-border" />}
            </div>
          ))}
        </div>

        <div className="max-h-[55vh] overflow-y-auto pr-1">
          {step === 1 && <StepIdentity state={state} setState={setState} errors={errors} />}
          {step === 2 && <StepMetadata state={state} setState={setState} errors={errors} />}
          {step === 3 && <StepSchedule state={state} setState={setState} errors={errors} />}
          {step === 4 && (
            <StepMembers
              state={state}
              setState={setState}
              errors={errors}
              isSuperUser={isSuperUser}
              excludedFromExtras={excludedFromExtras}
            />
          )}
          {step === 5 && <StepReview state={state} isSuperUser={isSuperUser} />}
        </div>

        {errors._root && (
          <p className="text-xs text-destructive">{errors._root}</p>
        )}

        <DialogFooter className="flex-row justify-between gap-2">
          <div>
            {step > 1 && (
              <Button type="button" variant="outline" onClick={goBack}>
                <ChevronRight className="ms-1 h-4 w-4" />
                قبلی
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={handleClose}>
              لغو
            </Button>
            {step < 5 ? (
              <Button type="button" onClick={goNext}>
                بعدی
                <ChevronLeft className="me-1 h-4 w-4" />
              </Button>
            ) : (
              <Button type="button" onClick={handleSubmit} disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <>
                    <Loader2 className="ms-1 h-4 w-4 animate-spin" />
                    در حال ایجاد...
                  </>
                ) : (
                  'ایجاد پروژه'
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

